/**
 * Mobile Quick Entry Drawer (Bottom Sheet)
 * Touch-optimized bottom sheet modal with large numeric steppers,
 * quick tag selectors, and instant AHA medical classification preview.
 */

import React, { useEffect } from 'react';
import { BloodPressureRecord, RecordFormData } from '@/types/record';
import { RecordForm } from '@/components/records/RecordForm';

export interface QuickEntryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RecordFormData) => Promise<void> | void;
  isLoading?: boolean;
  initialRecord?: BloodPressureRecord | null;
  defaultDate?: string;
}

export const QuickEntryDrawer: React.FC<QuickEntryDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  initialRecord = null,
  defaultDate,
}) => {
  // Prevent body scrolling when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-ink-950/70 backdrop-blur-xs animate-in fade-in duration-200">
      {/* Backdrop tap to close */}
      <div
        className="flex-1 w-full"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-up Bottom Sheet */}
      <div className="w-full max-w-lg mx-auto bg-white border-t-2 border-ink-900 rounded-t-lg shadow-2xl overflow-hidden flex flex-col max-h-[92vh] animate-in slide-in-from-bottom duration-250">
        {/* Drawer Drag Bar & Header */}
        <div className="px-5 pt-3 pb-3 bg-paper-200 border-b border-paper-400 flex items-center justify-between shrink-0">
          <div className="space-y-0.5">
            <div className="w-10 h-1 bg-paper-400 rounded-full mx-auto mb-2 sm:hidden" />
            <h3 className="font-serif font-bold text-base text-ink-900 tracking-wider uppercase">
              {initialRecord ? '編輯血壓紀錄' : '＋ 快速登錄血壓數據'}
            </h3>
            <p className="font-serif text-[11px] text-ink-500">
              AHA CLINICAL MOBILE REGISTRATION
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-ink-600 hover:text-ink-950 bg-paper-100 border border-paper-400 rounded-xs font-mono text-sm active:bg-paper-300"
            aria-label="關閉"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Form Content */}
        <div className="p-4 sm:p-5 overflow-y-auto overscroll-contain">
          <RecordForm
            onSubmit={async (data) => {
              await onSubmit(data);
              onClose();
            }}
            onCancel={onClose}
            initialRecord={initialRecord}
            isLoading={isLoading}
            defaultDate={defaultDate}
          />
        </div>
      </div>
    </div>
  );
};
